import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronDown, Sparkles, TrendingUp } from "lucide-react";

import { SERVICE_CONTENT } from "@/data/serviceContent";
import { TEAM_GROUP_PHOTOS } from "@/data/aiTeam";
import { IMG } from "@/data/imageLibrary";
import { PAGE_META } from "@/data/pageMeta";
import useDocumentHead from "@/lib/useDocumentHead";
import Reveal from "@/components/motion/Reveal";
import BrandGraphic from "@/components/motion/BrandGraphic";
import JapanGraphic from "@/components/motion/JapanGraphic";
import AnimatedCounter from "@/components/motion/AnimatedCounter";
import BrandWatermark from "@/components/BrandWatermark";
import EcosystemGlimpse from "@/components/EcosystemGlimpse";
import FeaturedCompletions from "@/components/FeaturedCompletions";
import FeaturedCompletionsHero from "@/components/FeaturedCompletionsHero";
import MilestoneRibbon from "@/components/MilestoneRibbon";
import LiveActivityTicker from "@/components/LiveActivityTicker";
import AITeamPreviewStrip from "@/components/AITeamPreviewStrip";
import TestimonialRotator from "@/components/TestimonialRotator";
import NBCOlympicsBadge from "@/components/NBCOlympicsBadge";
import SignboardsShowcase from "@/components/SignboardsShowcase";
import SEO, { PAGE_SEO } from "@/components/SEO";
import PageSideAura from "@/components/motion/PageSideAura";
import JsonLd, { organizationSchema, websiteSchema, faqSchema } from "@/components/JsonLd";

/**
 * Brand-level FAQs surfaced on the homepage. Same data array powers both:
 *   1. the visible <details>/<summary> disclosure list at the bottom of the
 *      page (operator-friendly onboarding for direct visitors), and
 *   2. the FAQPage schema.org JSON-LD block emitted in <head> so Google
 *      can render an expandable FAQ accordion in brand-name SERP results.
 *
 * Keep questions and answers identical between the two surfaces — Google
 * disqualifies pages from FAQ rich results when the structured-data text
 * does not appear visibly on the page.
 */
const HOME_FAQS = [
  {
    q: {
      en: "What does NoJo Enterprise do?",
      ja: "NoJo Enterpriseは何をしている会社ですか？",
    },
    a: {
      en: "NoJo Enterprise builds practical digital infrastructure for everyday life and business in Japan. We operate JapanETCcard.com (ETC card support for foreign residents), JapanTollReceipts.jp (automated NEXCO MEISAI toll-record reporting) and JapanChangeMoney.com (planned currency-exchange service).",
      ja: "NoJo Enterpriseは、日本の暮らしと事業の実務課題を解決するデジタルサービスを提供する会社です。在日外国人向けETCカードサポートのJapanETCcard.com、ETC利用明細・利用証明書の自動配信を支援するJapanTollReceipts.jp、準備中の通貨両替関連サービスJapanChangeMoney.comを運営しています。",
    },
  },
  {
    q: {
      en: "Where is NoJo Enterprise based?",
      ja: "NoJo Enterpriseはどこにありますか？",
    },
    a: {
      en: "Our office is in Yokosuka, Kanagawa, Japan — registered at 2-2-1 Honcho, Yokosuka 238-0041. We have operated from Yokosuka since the company was founded in 2013, and our customer base spans across Japan.",
      ja: "本社は神奈川県横須賀市に所在し、登記上の所在地は〒238-0041 神奈川県横須賀市本町 2-2-1 です。2013年の創業時より横須賀を拠点とし、お客様は日本全国にいらっしゃいます。",
    },
  },
  {
    q: {
      en: "Is NoJo Enterprise affiliated with NEXCO?",
      ja: "NoJo EnterpriseはNEXCOと提携していますか？",
    },
    a: {
      en: "No. NoJo Enterprise is an independent company. Neither NoJo Enterprise nor any of its operated services (JapanETCcard, JapanTollReceipts, JapanChangeMoney) is affiliated with NEXCO, the ETC Usage Inquiry Service (MEISAI), or any toll-road operator's official program.",
      ja: "いいえ。NoJo Enterpriseは独立した企業です。NoJo Enterprise本体および運営サービス（JapanETCcard・JapanTollReceipts・JapanChangeMoney）はいずれも、NEXCO、ETC利用照会サービス（MEISAI）、その他の高速道路事業者の公式プログラムとは提携しておりません。",
    },
  },
  {
    q: {
      en: "How long has NoJo Enterprise been operating?",
      ja: "NoJo Enterpriseはいつから事業を行っていますか？",
    },
    a: {
      en: "Since 2013. The first service, JapanETCcard, launched the same year. JapanTollReceipts joined the portfolio later, and JapanChangeMoney is currently in beta development with a 2027 launch target.",
      ja: "2013年から事業を行っています。最初のサービスJapanETCcardも同年に立ち上げました。その後JapanTollReceiptsを加え、現在はJapanChangeMoney（2027年公開予定）をベータ開発中です。",
    },
  },
  {
    q: {
      en: "What languages does NoJo Enterprise support?",
      ja: "NoJo Enterpriseの対応言語は？",
    },
    a: {
      en: "Both English and Japanese are first-class support languages across every NoJo Enterprise service. Customer-facing communications, dashboards, billing notes and email support are all delivered in the language each customer prefers.",
      ja: "NoJo Enterpriseの各サービスでは、日本語と英語の両方に対応しています。お客様向けのご案内、ダッシュボード、請求関連の通知、メールサポートなどを、ご希望の言語に合わせてお届けします。",
    },
  },
];

/**
 * Visible homepage FAQ disclosure list — paired 1:1 with HOME_FAQS data.
 */
function HomeFAQ({ lang, isJa }) {
  return (
    <section
      data-testid="home-faq"
      className="relative isolate bg-white border-t border-[#E2E8F0]"
    >
      <PageSideAura testId="home-faq-aura" />
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] uppercase font-bold mb-3">
          {isJa ? "よくあるご質問" : "Frequently asked questions"}
        </div>
        <h2
          className={[
            isJa ? "font-ja" : "font-display",
            "text-[#0F1D2D] text-2xl md:text-[32px] font-bold tracking-tight leading-tight mb-6",
          ].join(" ")}
        >
          {isJa
            ? "NoJo Enterpriseについてよくいただくご質問"
            : "Common questions about NoJo Enterprise"}
        </h2>
        <ul className="divide-y divide-[#E2E8F0] border border-[#E2E8F0] rounded-2xl overflow-hidden">
          {HOME_FAQS.map((f, i) => {
            const q = lang === "ja" ? f.q.ja : f.q.en;
            const a = lang === "ja" ? f.a.ja : f.a.en;
            return (
              <li key={i}>
                <details
                  className="group bg-white"
                  data-testid={`home-faq-item-${i}`}
                >
                  <summary className="flex items-start justify-between gap-4 px-5 md:px-6 py-4 md:py-5 cursor-pointer list-none transition-colors hover:bg-[#F8FAFC]">
                    <span
                      className={[
                        isJa ? "font-ja" : "font-display",
                        "text-[#0F1D2D] text-[15px] md:text-[16px] font-bold tracking-tight leading-snug pr-2",
                      ].join(" ")}
                    >
                      {q}
                    </span>
                    <ChevronDown
                      className="flex-shrink-0 mt-0.5 h-5 w-5 text-[#475569] transition-transform duration-200 group-open:rotate-180"
                      strokeWidth={2}
                      aria-hidden
                    />
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6 text-[14px] md:text-[15px] leading-[1.75] text-[#475569]">
                    {a}
                  </div>
                </details>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/**
 * Homepage rebuilt in Pfizer's editorial rhythm — no card chrome.
 *
 *   1. Featured banner (full-bleed photo + overlay copy)
 *   2. 3-up article rail (photo, eyebrow, headline, link — no borders)
 *   3. Services split-panels (alternating image/text rows, edge-to-edge)
 *   4. Customer support band (full-width navy strip with photo edge)
 *   5. Pipeline snapshot (color band + floating numbers + rules)
 *   6. Areas of Focus (tall portrait photos with overlay text)
 *   7. About row (image + flowing text, no borders)
 *   8. Commitment band (full-bleed photo overlay)
 */
export default function HomePage({ lang }) {
  const isJa = lang === "ja";
  // Title + meta — owned by <SEO /> via Helmet (see top of returned JSX).
  return (
    <>
      <SEO {...PAGE_SEO.home[lang]} path={`/${lang}`} lang={lang} />
      <JsonLd
        data={[
          organizationSchema(),
          websiteSchema({ lang }),
          faqSchema(
            HOME_FAQS.map((f) => ({
              question: lang === "ja" ? f.q.ja : f.q.en,
              answer: lang === "ja" ? f.a.ja : f.a.en,
            })),
          ),
        ]}
      />
      <FeaturedBanner lang={lang} isJa={isJa} />
      <LiveActivityTicker lang={lang} />
      <FeaturedCompletionsHero lang={lang} />
      <ServicesEditorial lang={lang} isJa={isJa} />
      <SignboardsShowcase lang={lang} variant="home" testIdPrefix="home-signboards-showcase" />
      <AITeamPreviewStrip lang={lang} />
      <TestimonialRotator
        lang={lang}
        mode="rotator"
        accent="#5B2E83"
        softBg="#F4EEF9"
        headlineEn="What our customers actually say."
        headlineJa="お客様の言葉が、いちばんの説明です。"
        linkTo={`/${lang}/testimonials`}
        testIdSurface="home"
      />
      <CustomerSupportBand lang={lang} isJa={isJa} />
      <AboutRow lang={lang} isJa={isJa} />
      <FoundersStrip lang={lang} isJa={isJa} />
      <CommitmentBand lang={lang} isJa={isJa} />
      <HomeFAQ lang={lang} isJa={isJa} />
    </>
  );
}

/* ===== 1. Featured banner ===== */

function FeaturedBanner({ lang, isJa }) {
  return (
    <section data-testid="home-featured" className="relative w-full bg-[#5B2E83] overflow-hidden" style={{ minHeight: "560px" }}>
      <img
        src={IMG.heroBanner}
        alt={isJa ? "横須賀ETC専用ゲート — NoJo Enterpriseが横須賀から日本全国のドライバーをサポート" : "Yokosuka ETC tollgate — NoJo Enterprise serves drivers across Japan from our Yokosuka home base"}
        className="absolute inset-0 w-full h-full object-cover opacity-80"
        style={{ objectPosition: "center 35%" }}
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />
      {/* Subtle neutral darkening on the LEFT-bottom only — just enough for
          headline legibility. No purple/blue color cast on the photo. */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/40 via-slate-900/10 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/25 via-transparent to-transparent" />

      {/* ANIM 1: Mobility flow line over hero (top-right) */}
      <BrandGraphic
        variant="mobility"
        color="#7AB8E8"
        className="absolute top-6 right-0 w-[55%] max-w-[640px] h-32 opacity-70 pointer-events-none hidden md:block"
        data-testid="home-hero-mobility-graphic"
      />
      {/* JAPAN ANIM: Mt. Fuji silhouette anchoring the bottom-right of hero */}
      <JapanGraphic
        variant="fuji"
        color="#1E3A5F"
        className="hidden md:block absolute -bottom-2 right-0 w-[420px] h-[180px] opacity-35 pointer-events-none"
        data-testid="home-hero-fuji"
      />
      {/* JAPAN ANIM: Seigaiha wave pattern bottom band */}
      <JapanGraphic
        variant="seigaiha"
        color="#7AB8E8"
        className="absolute bottom-0 left-0 w-full h-20 opacity-30 pointer-events-none"
        data-testid="home-hero-seigaiha"
      />
      {/* ANIM 2: Pulse dot near hero brand mark */}
      <span aria-hidden className="hidden md:block absolute top-6 left-10">
        <BrandGraphic variant="pulse" color="#7AB8E8" className="w-10 h-10" />
      </span>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 lg:pt-28 pb-8 md:pb-12 lg:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 max-w-2xl text-white animate-brand-fade-up">
          <div className="mb-3">
            <MilestoneRibbon lang={lang} />
          </div>
          <h1 data-testid="home-featured-title" className={[isJa ? "font-ja" : "font-display", "text-white text-4xl md:text-5xl lg:text-[58px] tracking-tighter font-bold leading-[1.05]"].join(" ")}>
            {isJa ? "日本の暮らしと仕事を、静かに支えるデジタル基盤を。" : "Practical digital infrastructure for everyday life in Japan."}
          </h1>
          <p className="mt-4 text-[16px] md:text-[17px] leading-[1.65] text-[#E2E8F0] max-w-xl">
            {isJa
              ? "モビリティ・記録・両替。NoJo Enterpriseは、日本全国のお客様基盤に向き合い、実務上の不便を解消するサービスを横須賀から構築しています。"
              : "Mobility, records and exchange. NoJo Enterprise serves a focused customer base across Japan, building from Yokosuka the practical services that resolve everyday operational friction."}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap items-center sm:items-start gap-4 sm:gap-3 w-full max-w-[320px] sm:max-w-none mx-auto sm:mx-0">
            <Link to={`/${lang}/whats-new`} data-testid="home-hero-whatsnew" className="w-full sm:w-auto inline-flex justify-center items-center gap-2 bg-[#663399] text-white px-6 py-3.5 rounded-full text-[13px] font-semibold tracking-tight hover:bg-[#552B82] transition-colors shadow-[0_10px_30px_-12px_rgba(102,51,153,0.7)]">
              {isJa ? "最新情報を見る" : "See What's New"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link to={`/${lang}/services`} data-testid="home-featured-cta" className="w-full sm:w-auto inline-flex justify-center items-center gap-2 bg-[#0E5FB0] text-white px-6 py-3.5 rounded-full text-[13px] font-semibold tracking-tight hover:bg-[#0B4D8F] transition-colors shadow-[0_10px_30px_-12px_rgba(14,95,176,0.7)]">
              {isJa ? "サービスを見る" : "Learn more"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link to={`/${lang}/about`} data-testid="home-featured-secondary" className="w-full sm:w-auto inline-flex justify-center items-center gap-2 bg-[#78D33C] text-white px-6 py-3.5 rounded-full text-[13px] font-semibold tracking-tight hover:bg-[#5FB825] transition-colors shadow-[0_10px_30px_-12px_rgba(120,211,60,0.7)]">
              {isJa ? "私たちについて" : "Who we are"}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </div>
          </div>

          {/* Big NoJo logo lockup — right column of hero.
              `translate-y` nudges the mark down so its top is fully
              contained within the hero photo (otherwise the purple
              corner crops above the image). */}
          <div
            className="hidden lg:flex lg:col-span-5 justify-center items-center relative animate-brand-fade-up lg:translate-y-10 xl:translate-y-16"
            style={{ animationDelay: "200ms" }}
            data-testid="home-hero-logo"
          >
            {/* Layered depth — soft outer glow + brand-tinted radial washes hide PNG edges */}
            <span
              aria-hidden
              className="absolute inset-0 rounded-full blur-3xl opacity-40 motion-safe:animate-brand-float"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, rgba(91,46,131,0.30) 0%, transparent 55%), radial-gradient(circle at 75% 65%, rgba(14,95,176,0.26) 0%, transparent 60%), radial-gradient(circle at 55% 80%, rgba(47,143,60,0.20) 0%, transparent 55%)",
                animationDelay: "0ms",
              }}
            />
            <span
              aria-hidden
              className="absolute inset-0 rounded-full blur-2xl opacity-35"
              style={{
                background:
                  "radial-gradient(circle at center, rgba(122,184,232,0.14) 0%, transparent 60%)",
              }}
            />
            {/* Soft inner halo right behind the mark */}
            <span
              aria-hidden
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[78%] h-[78%] rounded-full opacity-30 blur-xl"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)",
              }}
            />
            {/* Slow rotating tri-color conic ring — futuristic edge — disabled when reduced-motion */}
            <span
              aria-hidden
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[88%] h-[88%] rounded-full opacity-[0.15] blur-2xl motion-safe:animate-brand-orbit motion-reduce:animate-none"
              style={{
                background:
                  "conic-gradient(from 0deg, #5B2E83 0%, #0E5FB0 33%, #2F8F3C 66%, #5B2E83 100%)",
              }}
            />
            <img
              src="/nojo-logo.png"
              alt="NoJo Enterprise"
              width="420"
              height="420"
              className="relative w-[300px] xl:w-[360px] h-auto motion-safe:animate-brand-float"
              style={{
                /* Subtle, premium edge treatment — half the previous intensity */
                filter: [
                  "drop-shadow(0 22px 40px rgba(0,0,0,0.45))",
                  "drop-shadow(0 0 1px rgba(255,255,255,0.55))",
                  "drop-shadow(0 0 8px rgba(180,140,230,0.30))",
                  "drop-shadow(0 0 16px rgba(14,95,176,0.22))",
                  "drop-shadow(0 0 28px rgba(122,184,232,0.18))",
                ].join(" "),
              }}
              loading="eager"
              draggable={false}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===== 2. Article rail (no card chrome) ===== */

function ArticleRail({ lang, isJa }) {
  const items = isJa
    ? [
        { img: IMG.article1, eyebrow: "サービス", title: "JapanETCcard.com — 現場のお客様の声から学んだこと", body: "外国人居住者、米軍、大使館スタッフをサポートしてきた実例。", to: "/services/japanetccard" },
        { img: IMG.article2, eyebrow: "テクノロジー", title: "MEISAI連携の自動化と、毎朝のレポート配信", body: "JapanTollReceipts.jpがどのように99.97%の稼働率を実現しているか。", to: "/services/japantollreceipts" },
        { img: IMG.article3, eyebrow: "今後の展開", title: "JapanChangeMoney.com — 2027年公開予定", body: "既存のお客様基盤を活かした両替サービスを開発中。", to: "/services/japanchangemoney" },
      ]
    : [
        { img: IMG.article1, eyebrow: "Services", title: "JapanETCcard.com — What our customers taught us", body: "Real examples from over a decade supporting foreign residents, military families, and embassy staff.", to: "/services/japanetccard" },
        { img: IMG.article2, eyebrow: "Technology", title: "MEISAI automation and the morning toll-record drop", body: "How JapanTollReceipts.jp delivers 99.97% uptime without manual lookups.", to: "/services/japantollreceipts" },
        { img: IMG.article3, eyebrow: "What's coming", title: "JapanChangeMoney.com — planned launch in 2027", body: "A currency exchange service in development, built on the established customer base we already serve.", to: "/services/japanchangemoney" },
      ];

  return (
    <section data-testid="home-articles" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
          {items.map((a, i) => (
            <Reveal as="li" variant="up" delay={i * 140} key={i} data-testid={`home-article-${i + 1}`}>
              <Link to={`/${lang}${a.to}`} className="group block">
                <div className="aspect-[4/3] w-full overflow-hidden bg-[#F1F5F9] rounded-2xl">
                  <img src={a.img} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" />
                </div>
                <div className="pt-5">
                  <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] uppercase font-bold">
                    {a.eyebrow}
                  </div>
                  <h3 className={["mt-2.5", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-[20px] md:text-[22px] tracking-tight font-bold leading-snug group-hover:underline decoration-2 underline-offset-4"].join(" ")}>
                    {a.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-[1.75] text-[#475569]">
                    {a.body}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[12px] tracking-[0.16em] font-mono-display text-[#0E5FB0] uppercase font-bold">
                    {isJa ? "詳しく見る" : "Details"}
                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ===== 3. Services editorial split-panels ===== */

function ServicesEditorial({ lang, isJa }) {
  const services = [
    { slug: "japanetccard", planned: false },
    { slug: "japantollreceipts", planned: false },
    { slug: "japanchangemoney", planned: true },
  ];

  return (
    <section data-testid="home-products" className="relative isolate bg-white overflow-hidden">
      <PageSideAura testId="home-products-aura" />
      {/* Brand-colour blooms behind the intro — same 3-accent layered
          gradient used on the Services page intro, so the homepage band
          and the Services landing read as part of the same family. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-10 -left-24 w-[460px] h-[460px] rounded-full opacity-[0.08] blur-3xl"
        style={{ backgroundColor: "#5B2E83" }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute top-32 -left-8 w-[340px] h-[340px] rounded-full opacity-[0.08] blur-3xl"
        style={{ backgroundColor: "#2F8F3C" }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute top-72 left-40 w-[300px] h-[300px] rounded-full opacity-[0.06] blur-3xl hidden md:block"
        style={{ backgroundColor: "#0E5FB0" }}
      />
      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-7">
            {/* Top eyebrow row — same 3-dot brand-colour chip used on
                the Services page so the two intros visually rhyme. */}
            <div className="flex items-center gap-2.5 flex-wrap mb-4" data-testid="home-products-eyebrow-row">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white ring-1 ring-[#E2E8F0] shadow-[0_4px_14px_-6px_rgba(15,29,45,0.18)]"
                aria-hidden
              >
                <span className="inline-flex items-center -space-x-1">
                  <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: "#5B2E83" }} />
                  <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: "#2F8F3C" }} />
                  <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: "#0E5FB0" }} />
                </span>
                <span className="text-[11px] font-bold tracking-tight text-[#0F1D2D]">
                  {isJa ? "3 サービス" : "3 services"}
                </span>
              </span>
              <span className="text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] uppercase font-bold">
                {isJa ? "運営サービス" : "Our services"}
              </span>
            </div>
            {/* Headline with the gradient accent rail flanking on the left */}
            <div className="relative pl-4 md:pl-5">
              <span
                aria-hidden
                className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-gradient-to-b from-[#5B2E83] via-[#2F8F3C] to-[#0E5FB0]"
              />
              <h2 className={[isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] tracking-tighter font-bold leading-[1.1]"].join(" ")}>
                {isJa ? "それぞれのサービスは、現場のお客様課題から生まれています。" : "Every service starts with a real customer problem."}
              </h2>
            </div>
          </div>
          <div className="lg:col-span-5 flex lg:items-end lg:justify-end">
            <Link to={`/${lang}/services`} data-testid="home-products-viewall" className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] font-mono-display text-[#0F1D2D] uppercase font-bold hover:text-[#0E5FB0] transition-colors">
              {isJa ? "すべての運営サービスを見る" : "Browse all services"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </div>

      {services.map((p, i) => {
        const data = SERVICE_CONTENT[p.slug];
        const reverse = i % 2 === 1;
        const wordmark =
          p.slug === "japanetccard"
            ? IMG.japanetccardWordmark
            : p.slug === "japantollreceipts"
            ? IMG.japantollreceiptsWordmark
            : IMG.japanchangemoneyWordmark;
        const softBg =
          p.slug === "japanetccard"
            ? "#F4EEF9"
            : p.slug === "japantollreceipts"
            ? "#ECF6EC"
            : "#E8F0FA";
        return (
          <div
            key={p.slug}
            data-testid={`home-product-${p.slug}`}
            className="border-t border-[#E2E8F0]"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* WORDMARK PANEL — brand-color soft bg with the product wordmark.
                  Replaces the random lifestyle photo per owner directive: every
                  visitor should immediately recognize which product this is. */}
              <div className={["lg:col-span-6 order-1", reverse ? "lg:order-2" : ""].join(" ")}>
                <Link
                  to={`/${lang}/services/${p.slug}`}
                  data-testid={`home-product-wordmark-${p.slug}`}
                  className="block relative aspect-[5/4] w-full overflow-hidden rounded-2xl group transition-all hover:-translate-y-0.5 hover:shadow-[0_25px_55px_-25px_rgba(15,29,45,0.35)]"
                  style={{ backgroundColor: softBg }}
                >
                  <BrandGraphic
                    variant="wave"
                    color={data.accent}
                    className="absolute inset-0 w-full h-full opacity-[0.10] pointer-events-none"
                  />
                  <span aria-hidden className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: data.accent }} />
                  <div className="relative h-full w-full flex flex-col items-center justify-center px-8 md:px-12 py-8">
                    {/* Brand-anchor icon ABOVE the wordmark.
                        - JEC + JTR share the ETC gate
                        - JCM uses its two-hands currency-exchange icon */}
                    {(p.slug === "japanetccard" || p.slug === "japantollreceipts") && (
                      <img
                        src={IMG.etcGateBrand}
                        alt=""
                        aria-hidden="true"
                        className="block w-auto h-[140px] md:h-[170px] lg:h-[190px] mb-4 select-none"
                        draggable={false}
                        loading="lazy"
                      />
                    )}
                    {p.slug === "japanchangemoney" && (
                      <img
                        src={IMG.japanchangemoneyIcon}
                        alt=""
                        aria-hidden="true"
                        className="block w-auto h-[140px] md:h-[170px] lg:h-[190px] mb-4 select-none"
                        draggable={false}
                        loading="lazy"
                      />
                    )}
                    <img
                      src={wordmark}
                      alt={`${data.domain} wordmark`}
                      className="block max-w-[420px] w-full h-auto select-none"
                      draggable={false}
                      loading="lazy"
                    />
                    <div
                      className="mt-6 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold px-2.5 py-1 rounded-full transition-opacity opacity-80 group-hover:opacity-100"
                      style={p.planned ? { color: data.accent, border: `1px solid ${data.accent}` } : { color: "white", backgroundColor: "#2F8F3C" }}
                    >
                      {!p.planned && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white animate-brand-blink" />}
                      {p.planned ? (isJa ? "準備中" : "Planned") : (isJa ? "運営中" : "Live")}
                    </div>
                  </div>
                </Link>
              </div>
              <div className={["lg:col-span-6 order-2", reverse ? "lg:order-1" : ""].join(" ")}>
                <div className="flex items-center gap-3 mb-4">
                  <span aria-hidden className="h-2.5 w-2.5" style={{ backgroundColor: data.accent }} />
                  <span className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold" style={{ color: data.accent }}>
                    {`${data.eyebrow[lang]}`}
                  </span>
                </div>
                {/* Always Latin brand domain text (e.g. "JapanChangeMoney.com") in
                    both locales — always render with font-display (Inter Tight,
                    tighter letter-spacing) rather than font-ja. Noto Sans JP's
                    wider Latin glyph metrics + looser letter-spacing made this
                    unbreakable string overflow its grid column at 375px in the
                    JA locale even though the identical string fit fine in EN. */}
                <h3 className="font-display text-[#0F1D2D] text-3xl md:text-4xl lg:text-[42px] tracking-tighter font-bold leading-[1.08]">
                  {data.title[lang]}
                </h3>
                <p className="mt-3 text-[17px] md:text-[18px] font-display tracking-tight font-medium leading-snug" style={{ color: data.accent }}>
                  {data.headline[lang]}
                </p>
                <p className="mt-5 text-[15px] leading-[1.85] text-[#475569] max-w-xl">
                  {data.intro[lang]}
                </p>
                {/* Featured completions — live from /api/integrations/ecosystem/changelog */}
                {!p.planned && (
                  <div className="mt-6">
                    <FeaturedCompletions
                      lang={lang}
                      serviceId={p.slug}
                      limit={2}
                      compact
                      testIdPrefix={`home-product-${p.slug}-completions`}
                    />
                  </div>
                )}
                {/* ═══════════════════════════════════════════════════════════
                    JEC ONLY — Highlight note for the upcoming
                    JapanCardETC.com platform (in development, 2026 launch).
                    Owner directive 2026-05-19: surface this on the homepage
                    so visitors immediately learn that JapanETCcard's next
                    chapter is being built, and can preview the new site
                    under development.
                    ═════════════════════════════════════════════════════════ */}
                {p.slug === "japanetccard" && (
                  <div
                    className="mt-6 rounded-2xl border border-[#5B2E83]/25 bg-gradient-to-br from-[#F4EEF9] via-white to-[#EBF1FB] p-5 md:p-6 max-w-xl"
                    data-testid="home-product-japanetccard-next-gen-note"
                  >
                    <div className="flex items-center gap-2 mb-2.5">
                      <span
                        aria-hidden
                        className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#5B2E83] text-white"
                      >
                        <Sparkles className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                      <span className="text-[10px] tracking-[0.24em] font-mono-display uppercase font-bold text-[#5B2E83]">
                        {isJa ? "新サイト開発中 ─ 2026年公開予定" : "New site in development — launching 2026"}
                      </span>
                    </div>
                    <h4
                      className={[
                        isJa ? "font-ja" : "font-display",
                        "text-[#0F1D2D] text-[18px] md:text-[20px] font-bold tracking-tight leading-snug mb-2.5",
                      ].join(" ")}
                    >
                      {isJa
                        ? "次世代のJapanETCcardが、いま生まれています ─ JapanCardETC.com"
                        : "The next JapanETCcard is being built — JapanCardETC.com"}
                    </h4>
                    <p className="text-[13.5px] leading-[1.75] text-[#475569]">
                      {isJa
                        ? "100%自動化、AI主導、ゼロからの新設計 ─ 日本でお客様をサポートする、これからの形を目指しています。www.JapanCardETC.com は開発・検証用の一時的なドメインで、現在は新サイトをここで段階的に構築しています。検証完了後、現行 JapanETCcard.com の後継として段階的に切り替えます。"
                        : "100% automated, AI-driven, an entirely fresh concept and design — the future of how we will assist customers in Japan. www.JapanCardETC.com is a temporary development domain where the new site is being staged. Once integration testing is complete, it will succeed JapanETCcard.com."}
                    </p>
                    <a
                      href="https://www.japancardetc.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="home-product-japanetccard-next-gen-cta"
                      className="mt-4 inline-flex items-center gap-1.5 text-[12px] tracking-tight font-bold text-[#5B2E83] hover:text-[#3F1F5B] border-b-2 border-[#5B2E83] pb-0.5 transition-colors"
                    >
                      {isJa ? "開発中のサイトを見る" : "Preview the dev site"}
                      <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </a>
                  </div>
                )}
                <Link
                  to={`/${lang}/services/${p.slug}`}
                  data-testid={`home-product-cta-${p.slug}`}
                  className="mt-7 w-full sm:w-auto inline-flex justify-center items-center gap-2 px-5 h-12 rounded-full text-white text-[13px] font-semibold tracking-tight transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: data.accent }}
                >
                  {isJa ? "詳細ページを見る" : "View detail page"}
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

/* ===== 5. Customer support band ===== */

function CustomerSupportBand({ lang, isJa }) {
  // Pick the Mt. Fuji team group photo — most editorially confident.
  const teamPhoto = TEAM_GROUP_PHOTOS.find((p) => p.id === "mtfuji") || TEAM_GROUP_PHOTOS[0];
  return (
    <section data-testid="home-support" className="bg-[#F4EEF9] border-t border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-5">
          <div className="relative aspect-[5/4] w-full overflow-hidden rounded-2xl ring-1 ring-[#E2E8F0]">
            <img
              src={teamPhoto.src}
              alt={isJa ? "NoJo AI支援ワークフローのイメージ" : "NoJo AI team group photo"}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div aria-hidden className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-900/55 to-transparent" />
            <div className="absolute bottom-3 left-3 right-3 text-white">
              <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold">
                {isJa ? "私たちのAIチーム" : "Our AI team"}
              </div>
              <div className="text-[12px] opacity-85 mt-0.5">
                {isJa ? teamPhoto.locationJa : teamPhoto.locationEn}
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] font-bold uppercase">
            {isJa ? "お客様サポート" : "Customer support"}
          </div>
          <h2 className={["mt-3", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[42px] tracking-tighter font-bold leading-[1.08]"].join(" ")}>
            {isJa ? "わからない時に、頼れるサポート。" : "Help when you actually need it."}
          </h2>
          <p className="mt-5 text-[15px] md:text-[16px] leading-[1.85] text-[#475569] max-w-2xl">
            {isJa
              ? "NoJo Enterpriseは、お客様の状況に合わせて日本語と英語の両方で対応します。ETC・通行記録・出張精算・将来予定の両替サービスに関するご質問は、いつでもお気軽にどうぞ。"
              : "NoJo Enterprise speaks with each customer in Japanese or English — whichever helps. Questions about ETC cards, toll records, reimbursement reporting, or the planned exchange service are always welcome."}
          </p>
          <div className="mt-7 flex flex-wrap gap-x-8 gap-y-3">
            <Link to={`/${lang}/contact`} data-testid="home-support-cta" className="inline-flex items-center gap-2 text-[13px] tracking-[0.16em] font-mono-display uppercase font-bold text-[#5B2E83] hover:text-[#3F1F5B] border-b-2 border-[#5B2E83] pb-1">
              {isJa ? "お問い合わせ" : "Contact our team"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link to={`/${lang}/our-team`} data-testid="home-support-team" className="inline-flex items-center gap-2 text-[13px] tracking-[0.16em] font-mono-display uppercase font-bold text-[#0F1D2D] hover:text-[#5B2E83]">
              {isJa ? "AIチームを見る" : "Meet our AI team"}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===== 6. Pipeline band (floating stats on color) ===== */

function PipelineBand({ lang, isJa }) {
  const stats = isJa
    ? [
        { num: "2", numeric: 2, label: "運営中のサービス", to: "/services" },
        { num: "1", numeric: 1, label: "準備中のサービス", to: "/services/japanchangemoney" },
        { num: "全国対応", label: "お客様基盤", to: "/customers" },
        { num: "13年", numeric: 13, suffix: "年", label: "の運営実績", to: "/about#history" },
      ]
    : [
        { num: "2", numeric: 2, label: "Services live", to: "/services" },
        { num: "1", numeric: 1, label: "Service in development", to: "/services/japanchangemoney" },
        { num: "Nationwide", label: "Focused customer base", to: "/customers" },
        { num: "13 yrs", numeric: 13, suffix: " yrs", label: "Operating in Japan", to: "/about#history" },
      ];

  return (
    <section data-testid="home-pipeline" className="relative bg-[#0B4D8F] text-white overflow-hidden">
      <span aria-hidden className="absolute top-0 left-0 h-1 w-32 bg-[#5B2E83] animate-brand-pulse-soft" />
      <span aria-hidden className="absolute top-0 left-32 h-1 w-32 bg-[#2F8F3C] animate-brand-pulse-soft" style={{ animationDelay: "300ms" }} />
      <span aria-hidden className="absolute top-0 left-64 h-1 w-32 bg-[#0E5FB0] animate-brand-pulse-soft" style={{ animationDelay: "600ms" }} />

      {/* ANIM: faint topographic grid backdrop */}
      <BrandGraphic variant="grid" color="#7AB8E8" className="absolute inset-0 w-full h-full opacity-20 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="text-[10px] tracking-[0.3em] font-mono-display text-[#FFD166] uppercase font-bold mb-3">
          {isJa ? "サービススナップショット — 2026年5月時点" : "Service snapshot — as of May 2026"}
        </div>
        <h2 className={[isJa ? "font-ja" : "font-display", "text-white text-3xl md:text-4xl lg:text-[44px] tracking-tighter font-bold leading-[1.1] max-w-3xl"].join(" ")}>
          {isJa ? "現在の運営状況を、数字でご紹介します。" : "Where NoJo Enterprise stands today, by the numbers."}
        </h2>

        <ul className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-y-8 lg:gap-y-0 lg:divide-x lg:divide-white/10">
          {stats.map((s, i) => {
            const isNumeric = s.numeric != null;
            return (
              <li key={i} className="min-w-0">
                <Link
                  to={`/${lang}${s.to}`}
                  data-testid={`home-pipeline-${i + 1}`}
                  className="group block lg:px-8 first:lg:pl-0 last:lg:pr-0"
                >
                  <div
                    className={[
                      "font-display text-white font-bold tracking-tighter leading-[0.95] truncate",
                      isNumeric
                        ? "text-[56px] md:text-[64px] lg:text-[76px]"
                        : "text-[30px] md:text-[36px] lg:text-[40px]",
                    ].join(" ")}
                  >
                    {isNumeric ? (
                      <AnimatedCounter
                        to={s.numeric}
                        suffix={s.suffix || ""}
                        data-testid={`home-pipeline-${i + 1}-counter`}
                      />
                    ) : (
                      s.num
                    )}
                  </div>
                  <div className={["mt-3", isJa ? "font-ja" : "font-display", "text-[13px] md:text-[14px] text-[#CBD5E1] leading-snug max-w-[200px]"].join(" ")}>
                    {s.label}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#FFD166] group-hover:text-white">
                    {isJa ? "詳しく" : "Explore"}
                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/* ===== 7. Areas of focus — tall photos with overlay text ===== */

function FocusGrid({ lang, isJa }) {
  const cards = isJa
    ? [
        { img: IMG.audienceForeign, eyebrow: "対象者 / 01", title: "外国人居住者", color: "#5B2E83" },
        { img: IMG.audienceMilitary, eyebrow: "対象者 / 02", title: "米軍・SOFA関係者", color: "#0E5FB0" },
        { img: IMG.audienceFleet, eyebrow: "対象者 / 03", title: "法人・車両管理", color: "#2F8F3C" },
        { img: IMG.audienceJapanese, eyebrow: "対象者 / 04", title: "日本人ドライバー", color: "#0F1D2D" },
      ]
    : [
        { img: IMG.audienceForeign, eyebrow: "Audience / 01", title: "Foreign residents", color: "#5B2E83" },
        { img: IMG.audienceMilitary, eyebrow: "Audience / 02", title: "U.S. military & SOFA", color: "#0E5FB0" },
        { img: IMG.audienceFleet, eyebrow: "Audience / 03", title: "Business & fleet", color: "#2F8F3C" },
        { img: IMG.audienceJapanese, eyebrow: "Audience / 04", title: "Japanese drivers", color: "#0F1D2D" },
      ];

  return (
    <section data-testid="home-focus" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
          <div>
            <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] uppercase font-bold">
              {isJa ? "向き合うお客様" : "Areas of Focus"}
            </div>
            <h2 className={["mt-3", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] tracking-tighter font-bold leading-[1.1] max-w-2xl"].join(" ")}>
              {isJa ? "私たちのすべては、特定のお客様から始まります。" : "Everything starts with the people we serve."}
            </h2>
          </div>
          <Link to={`/${lang}/customers`} data-testid="home-focus-link" className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] font-mono-display text-[#0F1D2D] uppercase font-bold hover:text-[#0E5FB0] transition-colors">
            {isJa ? "すべての対象者を見る" : "View all customers"}
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {cards.map((c, i) => (
            <li key={i} data-testid={`home-focus-card-${i + 1}`}>
              <Link to={`/${lang}/customers`} className="group relative block aspect-[3/4] overflow-hidden rounded-2xl">
                <img src={c.img} alt={`${c.title} — NoJo Enterprise services in Japan`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]" loading="lazy" />
                <span aria-hidden className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                <span aria-hidden className="absolute top-0 left-0 h-1 w-16" style={{ backgroundColor: c.color }} />
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 text-white">
                  <div className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold text-[#A8C8E5]">
                    {c.eyebrow}
                  </div>
                  <h3 className={["mt-2", isJa ? "font-ja" : "font-display", "text-white text-[22px] md:text-[24px] font-bold tracking-tight leading-tight"].join(" ")}>
                    {c.title}
                  </h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold text-white opacity-90 group-hover:opacity-100">
                    {isJa ? "詳しく" : "View detail"}
                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ===== 8. About row (no card chrome) ===== */

function AboutRow({ lang, isJa }) {
  return (
    <section data-testid="home-about" className="relative isolate bg-[#F8F9FA]">
      <PageSideAura testId="home-about-aura" />
      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] uppercase font-bold">
            {isJa ? "NoJo Enterpriseについて" : "About NoJo Enterprise"}
          </div>
          <h2 className={["mt-3", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] tracking-tighter font-bold leading-[1.08]"].join(" ")}>
            {isJa ? "2013年、横須賀から始まった独立系デジタル企業です。" : "An independent digital company, established in Yokosuka in 2013."}
          </h2>
          <p className="mt-6 text-[15px] md:text-[16px] leading-[1.85] text-[#475569] max-w-xl">
            {isJa
              ? "NoJo Enterpriseは、神奈川県横須賀市に本社を置く独立系デジタルサービス企業です。代表取締役ジョーンズ・アダムのもと、創業以来一貫してモビリティ・記録・両替の各分野で日英バイリンガルの実務基盤を構築し、日本全国のお客様に向けて運営してまいりました。事業のすべては、現場で確かに観察されたお客様課題を出発点としています。"
              : "NoJo Enterprise is an independent digital services company headquartered in Yokosuka, Japan. Under the leadership of Representative Director Adam Jones, we have operated continuously since 2013, building bilingual operational infrastructure across mobility, records and exchange for a focused customer base throughout Japan. Every service we run begins with a clearly observed customer need."}
          </p>
          <div className="mt-7 flex flex-wrap gap-x-8 gap-y-3">
            <Link to={`/${lang}/company/profile`} data-testid="home-about-cta-profile" className="inline-flex items-center gap-2 text-[13px] tracking-[0.16em] font-mono-display uppercase font-bold text-[#0E5FB0] hover:text-[#084785] border-b-2 border-[#0E5FB0] pb-1">
              {isJa ? "会社概要" : "Learn more"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link to={`/${lang}/company/history`} data-testid="home-about-cta-history" className="inline-flex items-center gap-2 text-[13px] tracking-[0.16em] font-mono-display uppercase font-bold text-[#0F1D2D] hover:text-[#0E5FB0]">
              {isJa ? "沿革" : "Our history"}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          {/* Journey graphic — replaces the old stock-photo placeholder.
              Tells the 12-NOs → 1-YES → 18K customers (June 2026 milestone) → Tokyo 2020 story
              with NBC trust badge integration. */}
          <div
            className="relative rounded-2xl bg-white ring-1 ring-[#E2E8F0] overflow-hidden p-6 md:p-8"
            data-testid="home-about-journey"
            style={{
              background:
                "linear-gradient(135deg, #FAF7FE 0%, #FFFFFF 50%, #F2FAF3 100%)",
            }}
          >
            <div className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold text-[#5B2E83] mb-4">
              {isJa ? "私たちの道のり" : "Our journey"}
            </div>

            <ol className="space-y-5 relative">
              {/* Connector line */}
              <span
                aria-hidden
                className="absolute left-[18px] top-3 bottom-3 w-px"
                style={{
                  background:
                    "linear-gradient(to bottom, #DC2626 0%, #F59E0B 35%, #5B2E83 70%, #2F8F3C 100%)",
                }}
              />

              <li className="flex items-start gap-4">
                <span className="relative z-10 inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#FEE2E2] text-[#DC2626] font-bold text-[15px] shrink-0 ring-2 ring-white">
                  12×
                </span>
                <div className="pt-1.5">
                  <div className="text-[15px] font-bold text-[#0F1D2D] tracking-tight leading-snug">
                    {isJa ? "12回の「No」" : "Twelve “no”s"}
                  </div>
                  <div className="text-[12.5px] text-[#475569] leading-[1.6] mt-0.5">
                    {isJa
                      ? "銀行・NEXCO・行政窓口 ─ どこも首を縦に振りませんでした。"
                      : "Banks, NEXCO, government channels — they all said no."}
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <span className="relative z-10 inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#F4EEF9] text-[#5B2E83] font-bold text-[14px] shrink-0 ring-2 ring-white">
                  1×
                </span>
                <div className="pt-1.5">
                  <div className="text-[15px] font-bold text-[#0F1D2D] tracking-tight leading-snug">
                    {isJa ? "1回の「Yes」(2013)" : "One “yes” (2013)"}
                  </div>
                  <div className="text-[12.5px] text-[#475569] leading-[1.6] mt-0.5">
                    {isJa
                      ? "50枚のカード。米軍関係者とそのご家族向けのパイロット運用がスタート。"
                      : "Fifty cards. A pilot program for military families. The work began."}
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <span className="relative z-10 inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#EFF7F0] text-[#2F8F3C] shrink-0 ring-2 ring-white">
                  <TrendingUp className="h-4 w-4" strokeWidth={2.25} />
                </span>
                <div className="pt-1.5">
                  <div className="text-[15px] font-bold text-[#0F1D2D] tracking-tight leading-snug">
                    {isJa
                      ? "2026年6月、日本全国で18,000名超のお客様に到達"
                      : "June 2026 milestone — 18,000+ customers across Japan"}
                  </div>
                  <div className="text-[12.5px] text-[#475569] leading-[1.6] mt-0.5">
                    {isJa
                      ? "13年間、信頼を積み上げてきた結果です。"
                      : "Thirteen years of earning trust, one customer at a time."}
                  </div>
                </div>
              </li>
            </ol>

            {/* NBC Olympics trust callout — anchored at the bottom of the card */}
            <div className="mt-6 pt-5 border-t border-[#E2E8F0]">
              <NBCOlympicsBadge lang={lang} placement="row" testId="home-nbc" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===== 9. Commitment band ===== */

/* ===== 9b. Symbolic Japan customer-reach map ===== */
function ReachBand({ lang, isJa }) {
  const points = isJa
    ? [
        { label: "札幌", color: "#5B2E83" },
        { label: "仙台", color: "#2F8F3C" },
        { label: "首都圏 (横須賀本社)", color: "#0E5FB0", anchor: true },
        { label: "名古屋", color: "#5B2E83" },
        { label: "大阪・京都", color: "#2F8F3C" },
        { label: "広島", color: "#0E5FB0" },
        { label: "福岡", color: "#5B2E83" },
        { label: "沖縄", color: "#2F8F3C" },
      ]
    : [
        { label: "Sapporo", color: "#5B2E83" },
        { label: "Sendai", color: "#2F8F3C" },
        { label: "Greater Tokyo (Yokosuka HQ)", color: "#0E5FB0", anchor: true },
        { label: "Nagoya", color: "#5B2E83" },
        { label: "Osaka / Kyoto", color: "#2F8F3C" },
        { label: "Hiroshima", color: "#0E5FB0" },
        { label: "Fukuoka", color: "#5B2E83" },
        { label: "Okinawa", color: "#2F8F3C" },
      ];

  return (
    <section data-testid="home-reach" className="bg-white border-t border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <Reveal as="div" variant="up" className="lg:col-span-6">
          <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] uppercase font-bold mb-3">
            {isJa ? "お客様の広がり" : "Customer reach"}
          </div>
          <h2 className={[isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.1] max-w-xl"].join(" ")}>
            {isJa ? "日本全国に広がる、特定のお客様基盤。" : "A focused customer base across Japan."}
          </h2>
          <p className="mt-4 text-[15px] md:text-[16px] leading-[1.7] text-[#475569] max-w-xl">
            {isJa
              ? "私たちのお客様は、北海道から沖縄まで、日本各地でサービスをご利用くださっています。"
              : "Our customers operate across Japan — from Hokkaido in the north to Okinawa in the south."}
          </p>
          <p className="mt-3 text-[12.5px] leading-relaxed text-[#94A3B8] max-w-xl">
            {isJa
              ? "本マップは象徴的な表現であり、特定のお客様の所在地を示すものではありません。"
              : "This map is symbolic. It does not reveal any individual customer's location."}
          </p>
          <ul data-testid="home-reach-points" className="mt-7 grid grid-cols-2 gap-x-6 gap-y-2.5 max-w-md">
            {points.map((p, i) => (
              <li key={i} className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className={p.anchor ? "h-2.5 w-2.5 rounded-full ring-2 ring-offset-1 ring-offset-white" : "h-2 w-2 rounded-full"}
                  style={{ backgroundColor: p.color, boxShadow: `0 0 8px -1px ${p.color}99` }}
                />
                <span className={["text-[13px] text-[#0F1D2D] tracking-tight", p.anchor ? "font-bold" : "font-medium"].join(" ")}>
                  {p.label}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal as="div" variant="scale" delay={140} className="lg:col-span-6">
          <div className="relative aspect-square max-w-[460px] mx-auto">
            <JapanGraphic
              variant="japan-reach"
              color="#0F1D2D"
              className="w-full h-full"
              data-testid="home-reach-map"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ===== 9.5 Founder's message strip =====
   Surfaces the per-service founder narratives as a small, scannable
   homepage block: portrait on the left, two link cards on the right
   (one per service that has a long-form founder's message authored
   in /data/foundersMessages.js). Designed to highlight the human
   story behind the brand without dominating the homepage. */
function FoundersStrip({ lang, isJa }) {
  const cards = [
    {
      slug: "japanetccard",
      accent: "#0E5FB0",
      labelEn: "JapanETCcard",
      labelJa: "JapanETCcard",
      titleEn: "Why we built JapanETCcard",
      titleJa: "なぜJapanETCcardを立ち上げたのか",
      teaserEn: "Twelve closed doors, fifteen years of work, and the journey to a Japan-national ETC partnership.",
      teaserJa: "12回の門前払い、15年にわたる積み上げ、そして日本のETC事業者との本物のパートナーシップに至るまで。",
    },
    {
      slug: "japantollreceipts",
      accent: "#2F8F3C",
      labelEn: "JapanTollReceipts",
      labelJa: "JapanTollReceipts",
      titleEn: "Why we built JapanTollReceipts",
      titleJa: "なぜJapanTollReceiptsを立ち上げたのか",
      teaserEn: "An automation and intelligence layer built alongside Japan's MEISAI system — scheduled reports, vehicle detection, smart alerts.",
      teaserJa: "MEISAIという信頼の基盤の上に重ねる自動化レイヤー ─ 定期配信、車両自動特定、スマートアラート。",
    },
  ];

  return (
    <section
      data-testid="home-founders-strip"
      className="relative isolate bg-white border-t border-[#E2E8F0]"
    >
      <PageSideAura testId="home-founders-aura" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* LEFT — portrait + identity */}
          <Reveal as="div" variant="up" className="lg:col-span-4">
            <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] uppercase font-bold mb-4" data-testid="home-founders-eyebrow">
              {isJa ? "創業者からのメッセージ" : "From the Founder"}
            </div>
            <div className="relative inline-block">
              <img
                src="/founder/adam-jones.png"
                alt={isJa ? "アダム・ジョーンズ ─ 創業者・代表取締役CEO" : "Adam Jones — Founder & CEO"}
                width="160"
                height="160"
                loading="lazy"
                className="h-32 w-32 md:h-40 md:w-40 rounded-2xl object-cover ring-2 ring-white shadow-[0_18px_44px_-18px_rgba(15,29,45,0.35)]"
                style={{ outline: "3px solid #5B2E83", outlineOffset: "-3px" }}
                data-testid="home-founders-portrait"
              />
              <span
                aria-hidden
                className="absolute -bottom-1.5 left-0 h-1 w-12 rounded-full bg-[#78D33C]"
              />
            </div>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "mt-6 text-[#0F1D2D] text-2xl md:text-[28px] font-bold tracking-tight leading-tight",
              ].join(" ")}
              data-testid="home-founders-name"
            >
              {isJa ? "アダム・ジョーンズ" : "Adam Jones"}
            </h2>
            <p className="mt-1 text-[13px] md:text-[14px] text-[#475569] font-medium">
              {isJa ? "創業者・代表取締役CEO" : "Founder & CEO"}
            </p>
            <p className="mt-5 text-[14px] md:text-[15px] leading-[1.75] text-[#475569] max-w-md" data-testid="home-founders-intro">
              {isJa
                ? "アダムが、NoJo Enterpriseが運営する各サービス ─ JapanETCcardとJapanTollReceipts ─ がどのようにして生まれたのか、そしてこれからどこへ向かうのかを、自身の言葉で語ります。"
                : "Adam shares — in his own words — how each of NoJo Enterprise's services came to exist, the people who made them possible, and where the journey leads next."}
            </p>
          </Reveal>

          {/* RIGHT — service cards linking to each long-form message */}
          <Reveal as="div" variant="up" delay={120} className="lg:col-span-8 space-y-4 md:space-y-5">
            {cards.map((card) => (
              <Link
                key={card.slug}
                to={`/${lang}/founders-message/${card.slug}`}
                className="group flex items-center gap-5 md:gap-7 rounded-2xl ring-1 ring-[#E2E8F0] bg-white p-5 md:p-6 hover:bg-[#FAFBFD] hover:ring-[#CBD5E1] transition-all shadow-[0_2px_10px_-6px_rgba(15,29,45,0.08)] hover:shadow-[0_18px_38px_-22px_rgba(15,29,45,0.25)]"
                data-testid={`home-founders-card-${card.slug}`}
              >
                <div
                  className="hidden sm:flex items-center justify-center h-14 w-14 md:h-16 md:w-16 rounded-xl shrink-0 font-display font-bold text-white text-lg md:text-xl tracking-tight"
                  style={{ backgroundColor: card.accent }}
                  aria-hidden
                >
                  {card.slug === "japanetccard" ? "JEC" : "JTR"}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[10px] md:text-[10.5px] tracking-[0.22em] uppercase font-bold font-mono-display mb-1.5"
                    style={{ color: card.accent }}
                  >
                    {isJa ? card.labelJa : card.labelEn}
                  </div>
                  <h3
                    className={[
                      isJa ? "font-ja" : "font-display",
                      "text-[#0F1D2D] text-lg md:text-[22px] font-bold leading-tight tracking-tight",
                    ].join(" ")}
                  >
                    {isJa ? card.titleJa : card.titleEn}
                  </h3>
                  <p className="mt-1.5 text-[13px] md:text-[14px] text-[#475569] leading-snug">
                    {isJa ? card.teaserJa : card.teaserEn}
                  </p>
                </div>
                <ArrowRight
                  className="hidden md:block h-5 w-5 text-[#94A3B8] group-hover:translate-x-1 group-hover:text-[#0F1D2D] transition-all shrink-0"
                  aria-hidden
                />
              </Link>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
}


/* ===== 10. Commitment band ===== */
function CommitmentBand({ lang, isJa }) {  return (
    <section data-testid="home-commitment" className="relative w-full bg-[#5B2E83] overflow-hidden" style={{ minHeight: "580px" }}>
      <img src={IMG.commitment} alt={isJa ? "横須賀のETC料金所 — NoJo Enterpriseが横須賀の本社からサービスに横断する学びと、お客様データを守る取り組みを支えています" : "Yokosuka tollgate — NoJo Enterprise's Yokosuka home base, where lessons travel across services and customer data is protected"} className="absolute inset-0 w-full h-full object-cover opacity-60" style={{ objectPosition: "center 55%" }} loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/55 via-slate-900/15 to-transparent" />

      {/* ANIM: editorial wave at top edge */}
      <BrandGraphic variant="wave" color="#7AB8E8" className="absolute top-0 left-0 w-full h-16 opacity-70 pointer-events-none" />
      {/* ANIM: orbit graphic top-right */}
      <BrandGraphic variant="orbit" color="#7AB8E8" className="hidden md:block absolute top-10 right-6 w-44 h-44 opacity-50 pointer-events-none" data-testid="home-commitment-orbit" />

      {/* Brand ghost watermark — anchors corporate identity behind the commitment headline */}
      <BrandWatermark
        variant="ghost"
        position="bottom-right"
        size={420}
        tone="dim"
        testId="home-commitment-watermark"
      />
      <BrandWatermark
        variant="corner"
        position="top-left"
        size={38}
        tone="auto"
        testId="home-commitment-corner-mark"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <Reveal className="max-w-2xl text-white" variant="up">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="h-1.5 w-1.5 bg-[#7AB8E8]" />
            <span className="text-[10px] tracking-[0.3em] font-mono-display uppercase font-bold text-[#A8C8E5]">
              {isJa ? "私たちのお約束" : "Our commitment"}
            </span>
          </div>
          <h2 className={[isJa ? "font-ja" : "font-display", "text-white text-3xl md:text-4xl lg:text-[44px] tracking-tighter font-bold leading-[1.1]"].join(" ")}>
            {isJa ? "別々のサービス。承認された知見の活用。お客様データの保護。" : "Separate products. Shared approved lessons. Protected customer data."}
          </h2>
          <p className="mt-5 text-[15px] md:text-[16px] leading-[1.85] text-[#CBD5E1] max-w-xl">
            {isJa
              ? "NoJo Enterpriseは、それぞれの運営サービスを独立して運営し、お客様データを商用目的で共有することはありません。透明性のあるレポート、機微情報の安全な取扱い、最小限のデータ保持を運用に組み込んでいます。"
              : "Each operated service runs independently. We don't share customer data for commercial purposes. Transparent reporting, careful handling of sensitive information and minimal data retention are built into our defaults."}
          </p>
          <Link to={`/${lang}/trust-security`} data-testid="home-commitment-cta" className="mt-8 inline-flex items-center gap-2 bg-white text-[#0F1D2D] px-6 py-3.5 rounded-md text-[13px] font-semibold tracking-tight hover:bg-[#F1F5F9] transition-colors">
            {isJa ? "信頼とセキュリティを見る" : "Learn more about our commitment"}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

